import React, { useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { uploadOrdersFile, clearUploadResult } from '../../store/slices/orderManagementSlice';
import { Upload, FileText, CheckCircle, AlertTriangle, X } from 'lucide-react';

interface OrderUploadProps {
  onUploadComplete?: () => void;
}

const OrderUpload: React.FC<OrderUploadProps> = ({ onUploadComplete }) => {
  const dispatch = useAppDispatch();
  const { uploadLoading, uploadResult } = useAppSelector((state) => state.orderManagement);
  
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    // Validate file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['xlsx', 'xls', 'csv'];

    if (!allowedExtensions.includes(fileExtension || '')) {
      alert('Please select a valid Excel file (.xlsx, .xls) or CSV file (.csv)');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    // Check if file is empty
    if (file.size === 0) {
      alert('The selected file is empty. Please choose a file with data.');
      return;
    }

    setSelectedFile(file);
  };
  const handleUpload = async () => {
    if (selectedFile) {
      try {
        await dispatch(uploadOrdersFile(selectedFile)).unwrap();
        // Call onUploadComplete immediately after successful upload
        if (onUploadComplete) {
          onUploadComplete();
        }
      } catch (error) {
        console.error('Upload failed:', error);
        // Error will be displayed through the Redux state
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };
  const handleCloseResult = () => {
    dispatch(clearUploadResult());
    // Note: onUploadComplete is now called immediately after successful upload
    // No need to call it again when closing the result modal
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      {!selectedFile ? (
        <div
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <Upload className="mx-auto h-12 w-12 text-gray-400" />        <div className="mt-4">
            <p className="text-lg font-medium text-gray-900">Upload Order File</p>
            <p className="text-sm text-gray-500 mt-1">
              Upload your order file with Order #, Item, and SKU columns
            </p>
          </div>
          <div className="mt-6">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FileText className="h-4 w-4 mr-2" />
              Select File
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      ) : (
        /* Selected File Preview */
        <div className="border rounded-lg p-6 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileText className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {!uploadLoading && (
                <>
                  <button
                    onClick={handleRemoveFile}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                  <button
                    onClick={handleUpload}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Upload
                  </button>
                </>
              )}
            </div>
          </div>
          
          {uploadLoading && (
            <div className="mt-4">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-600">Processing file...</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* File Format Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Expected File Format</h4>
        <div className="text-sm text-blue-800 space-y-1">
          <p>• Excel (.xlsx, .xls) or CSV (.csv) file</p>          <p>• Required columns: <strong>Order #</strong>, <strong>Item</strong>, <strong>SKU</strong></p>
          <p>• Optional columns: Value, is Fulfilled</p>
          <p>• Alternative column names accepted: Order, Title, VALUE, IS FULFILLED</p>
          <p>• SKU format: ABC12345-UK8-Color</p>
          <p>• Order # can repeat for multiple items in same order</p>
          <p>• Gift vouchers and gift cards will be automatically skipped</p>
          <p>• Maximum file size: 10MB</p>
          <div className="mt-2 p-2 bg-blue-100 rounded text-xs">
            <p><strong>Example rows:</strong></p>
            <table className="text-xs mt-1 w-full">
              <thead>
                <tr className="font-semibold">
                  <td>Order #</td>
                  <td>Item</td>
                  <td>SKU</td>
                  <td>Value</td>
                  <td>is Fulfilled</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>CLB519919</td>
                  <td>Scoop Neck Bodysuit</td>
                  <td>GFW23669-UK8-Beige</td>
                  <td>5,280.00</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>CLB519919</td>
                  <td>High Rise Jean</td>
                  <td>GFJ18918-UK10-Black</td>
                  <td>4,500.00</td>
                  <td>No</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Upload Result */}
      {uploadResult && (
        <div className={`border rounded-md p-4 ${
          uploadResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex justify-between items-start">
            <div className="flex">
              {uploadResult.success ? (
                <CheckCircle className="h-5 w-5 text-green-400" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-red-400" />
              )}
              <div className="ml-3">
                <h3 className={`text-sm font-medium ${
                  uploadResult.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {uploadResult.success ? 'Upload Successful' : 'Upload Failed'}
                </h3>
                <p className={`text-sm mt-1 ${
                  uploadResult.success ? 'text-green-700' : 'text-red-700'
                }`}>
                  {uploadResult.message}
                </p>
                  {uploadResult.success && (
                  <div className="mt-2 space-y-1 text-sm text-green-700">
                    <p>• Orders processed: {uploadResult.total_orders}</p>
                    <p>• Items processed: {uploadResult.total_items}</p>
                    {uploadResult.invalid_items > 0 && (
                      <p className="text-orange-700">• Invalid items: {uploadResult.invalid_items}</p>
                    )}
                    {uploadResult.skipped_gift_vouchers && (
                      <p className="text-blue-700">• Skipped gift vouchers: {uploadResult.skipped_gift_vouchers}</p>
                    )}
                  </div>
                )}

                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-sm font-medium text-red-800">Errors found in file:</h4>
                    <div className="mt-1 max-h-32 overflow-y-auto">
                      {uploadResult.errors.slice(0, 10).map((error, index) => (
                        <p key={index} className="text-xs text-red-700">
                          Row {error.row}: {error.error}
                        </p>
                      ))}
                      {uploadResult.errors.length > 10 && (
                        <p className="text-xs text-red-600 font-medium">
                          ... and {uploadResult.errors.length - 10} more errors
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleCloseResult}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderUpload;
